import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Handshake, Loader2, CheckCircle } from "lucide-react";

export const PartnerInquiry = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website_url: "",
    message: "",
    partnership_type: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('partner-inquiry', {
        body: formData,
      });

      if (error) throw error;

      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      
      // Reset form
      setFormData({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        website_url: "",
        message: "",
        partnership_type: "",
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="pt-12 pb-12">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
              <p className="text-muted-foreground mb-6">
                We've received your partnership inquiry and will review it shortly.
                Our team will get back to you within 1-2 business days.
              </p>
              <Button onClick={() => setSubmitted(false)} variant="outline">
                Submit Another Inquiry
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Handshake className="w-10 h-10 text-primary" />
              <h2 className="text-4xl md:text-5xl font-bold">Partner With Us</h2>
            </div>
            <p className="text-xl text-muted-foreground">
              Collaborate with Launch To Revenue to create value for creators worldwide
            </p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle>Partnership Inquiry</CardTitle>
              <CardDescription>
                Whether you're interested in co-trainings, product collaborations, or service partnerships,
                we'd love to hear from you!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      placeholder="Your Company Inc."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Your Name *</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website_url">Website URL</Label>
                  <Input
                    id="website_url"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://yourcompany.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="partnership_type">Partnership Type *</Label>
                  <Select
                    value={formData.partnership_type}
                    onValueChange={(value) => setFormData({ ...formData, partnership_type: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select partnership type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="co-training">Co-Training / Joint Program</SelectItem>
                      <SelectItem value="product">Product Collaboration</SelectItem>
                      <SelectItem value="service">Service Partnership</SelectItem>
                      <SelectItem value="affiliate">Affiliate Program</SelectItem>
                      <SelectItem value="sponsorship">Sponsorship Opportunity</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Tell Us About Your Partnership Idea *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    placeholder="Describe how you'd like to collaborate with Launch To Revenue..."
                    rows={6}
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full" size="lg">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Handshake className="mr-2 h-4 w-4" />
                      Submit Partnership Inquiry
                    </>
                  )}
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  We'll review your inquiry and respond within 1-2 business days
                </p>
              </form>
            </CardContent>
          </Card>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Co-Trainings</h3>
                <p className="text-sm text-muted-foreground">
                  Create joint programs that deliver value to our community
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Product Partnerships</h3>
                <p className="text-sm text-muted-foreground">
                  Integrate your tools and services with our platform
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Service Collaborations</h3>
                <p className="text-sm text-muted-foreground">
                  Offer complementary services to our creator community
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerInquiry;